import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OutputRefSubscription,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
// import { makeStateKey, TransferState } from '@angular/core'
import { format, parse } from 'date-fns';
import { debounceTime, distinctUntilChanged, min, Subscription, take } from 'rxjs';
import { getDate } from '@app/helpers/date';
import { DrupalService } from '@app/services/drupal.service';
import { DataService } from '@app/services/data.service';
import { ApiService } from '@app/services/api.service';
import { GaService } from '@app/services/ga.service';
import { FormAutofillService } from '@app/services/form-autofill.service';
import { KeyValue, WebformService } from '@app/services/webform.service';
import {
  MobiUiCheckboxGroupComponent,
  MobiUiCheckboxLayoutType,
  MobiUiDatepickerComponent,
  MobiUiRadioButtonLayoutType,
  MobiUiTypeaheadComponent,
} from '@mobi/rwc-ui-components-ng-jslib';
import { setTimeout$ } from '@app/helpers/timeout';
import { BreakpointService } from '@app/services/breakpoint.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { outputToObservable } from '@angular/core/rxjs-interop';

// const ZIP_LOCATIONS = makeStateKey('zip_locations')

@Component({
  standalone: false,
  selector: 'webform-field',
  templateUrl: './field.component.html',
  styleUrls: ['./field.component.scss'],
})
export class FieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() onSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Input() field!: any;
  @Input() parentGroup!: FormGroup;
  @ViewChild('datepicker') datepicker!: MobiUiDatepickerComponent;
  @ViewChild('checkboxes') checkboxes!: MobiUiCheckboxGroupComponent;
  @ViewChild(MobiUiTypeaheadComponent) typeahead: MobiUiTypeaheadComponent | undefined;
  @HostBinding('class')
  get hostClasses() {
    return `${this.type}`;
  }
  MobiUiRadioButtonLayoutType = MobiUiRadioButtonLayoutType;
  MobiUiCheckboxLayoutType = MobiUiCheckboxLayoutType;

  id: string = '';
  icon: string = '';
  background: string = '';
  width: string = '';
  type: string = '';
  title: string = '';
  placeholder: string = '';
  box: boolean = false;
  inline: boolean = false;
  isCard: boolean = false;
  question: boolean = false;
  readonly: boolean = false;
  disabled: boolean = false;
  submittable: boolean = false;
  statesClear: boolean = true;
  isSum: boolean = false;
  titleDisplay!: string | null;
  errorValues!: any;
  minDate: any = null;
  maxDate: any = null;
  maxDateError: string = '';
  minDateError: string = '';
  zipLocations: any[] = [];
  disabledOptions: string[] = [];
  disableDatesFn: ((date: Date) => boolean) | undefined = undefined;

  // Fields that only display data and do not have logic
  excludedFields: string[] = [
    'container',
    'details',
    'webform_flexbox',
    'webform_actions',
    'processed_text',
    'webform_markup',
    'webform_link',
    'mobi_webform_primcalc_gabox',
    'webform_horizontal_rule',
    'mobi_webform_primcalc_calc',
    'mobi_webform_infobox',
    'mobi_webform_primcalc_pdfgenerate',
    'mobi_webform_primcalc_summary',
    'mobi_webform_primcalc_dsebox',
    'mobi_webform_login_banner',
    'mobi_webform_compliance_guidelines',
    'mobi_webform_illustration_section',
  ];
  boxClasses: string =
    'mobi-ui-card-silent webform-box is-row mobi-ui-ink-default !items-start';
  inlineDate!: Date;
  initialDate!: Date;
  #formDataSub!: Subscription;
  #typeaheadSub?: OutputRefSubscription;
  #breakpointSub?: Subscription;
  #typeaheadSelectSub?: OutputRefSubscription;

  readonly #route = inject(DrupalService);
  readonly #data = inject(DataService);
  readonly api = inject(ApiService);
  readonly #webform = inject(WebformService);
  readonly ga = inject(GaService);
  readonly #autofill = inject(FormAutofillService);
  readonly breakpoint = inject(BreakpointService);
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      if (this.field[`#date_max_error_${this.#route.language()}`]) {
        this.maxDateError = this.field[`#date_max_error_${this.#route.language()}`];
      }
      if (this.field[`#date_min_error_${this.#route.language()}`]) {
        this.minDateError = this.field[`#date_min_error_${this.#route.language()}`];
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.id = this.field['#webform_id'];
    this.type = this.field['#type'] || '';
    this.title = this.field['#title'] || '';
    this.placeholder = this.field['#placeholder'] || '';
    this.readonly = this.field['#readonly'] || false;
    this.disabled = this.field['#disabled'] || false;
    this.titleDisplay = this.field['#title_display'] || null;
    this.errorValues = {
      name: this.title,
    };

    this.statesClear = this.field['#states_clear'] === 'false' ? false : true;

    // custom webform settings
    this.icon = this.field['#icon'];
    this.width = this.field['#width'];
    this.inline = this.field['#inline'] || false;
    this.box = this.field['#box'] || false;
    this.background = this.field['#background'];
    this.question = this.field['#question'] || false;
    this.isCard = this.field['#is_card'] || false;
    this.isSum = this.field['#is_sum'] || false;
    this.submittable = this.field['#submittable'] || false;
    this.disabledOptions = this.field['#disabled_opts'] || [];

    if (this.field['#pattern']) this.errorValues.pattern = this.field['#pattern'];
    if (this.field['#min']) this.errorValues.minVal = this.field['#min'];
    if (this.field['#max']) this.errorValues.maxVal = this.field['#max'];
    if (this.field['#step']) this.errorValues.stepVal = this.field['#step'];
    if (this.field['#minlength']) this.errorValues.minVal = this.field['#minlength'];
    if (this.field['#maxlength']) this.errorValues.maxVal = this.field['#maxlength'];
    if (this.field['#multiple']) this.errorValues.maxCount = this.field['#multiple'];
    if (this.field['#max_filesize']) {
      this.errorValues.maxSize = this.field['#max_filesize'];
    }
    if (this.field['#file_extensions']) {
      this.errorValues.allowedTypes = this.field['#file_extensions'];
    }
    if (this.field['#date_date_min']) {
      this.minDate = getDate(this.field['#date_date_min']);
      this.inlineDate = this.minDate;
      this.errorValues.minVal = format(this.minDate, 'dd.MM.yyyy');
    }

    if (this.field['#date_date_max']) {
      this.maxDate = getDate(this.field['#date_date_max']);
      this.errorValues.maxVal = format(this.maxDate, 'dd.MM.yyyy');
      this.initialDate = this.maxDate;
    }

    if (this.field['#default_value'] && this.field['#type'] === 'date') {
      this.initialDate = getDate(this.field['#default_value']);
    }

    if (this.type === 'date' && this.field['#date_days']) {
      const enabledDays = this.field['#date_days'].map(Number);
      this.disableDatesFn = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        return !enabledDays.includes(dayOfWeek);
      };
    }

    if (this.field['#inline']) {
      this.#formDataSub = this.#webform.formData
        .pipe(take(1))
        .subscribe((v: KeyValue) => {
          if (v[this.id]) {
            this.inlineDate = parse(v[this.id], 'dd.MM.yyyy', new Date());
          } else {
            if (this.minDate) {
              this.inlineDate = this.minDate;
            } else {
              this.inlineDate = new Date();
            }
          }
        });
      this.parentGroup.get(this.id)?.setValue(format(this.inlineDate, 'dd.MM.yyyy'));
    }
  }

  ngAfterViewInit() {
    // Activate submittable radios if on last page
    if (this.submittable) {
      const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
        '.radios-submittable input'
      );
      inputs.forEach((input: HTMLInputElement) => {
        input.addEventListener('click', (e: any) => {
          // make sure value has changed
          this.#webform.submissionType = e.target?.value || '';
          setTimeout$(() => this.submit(), 500);
        });
      });
    }

    if (this?.type === 'mobi_ziploc') {
      this.#typeaheadSub = this.typeahead?.searchTermChange.subscribe(
        async (term: string) => {
          if (term.length < 1) {
            this.ga.autocompleteList.set([]);
            return;
          }
          await this.ga.updateAutocomplete(term);
        }
      );

      if (this.typeahead) {
        this.#typeaheadSelectSub = outputToObservable(this.typeahead.searchTermChange)
          .pipe(debounceTime(150), distinctUntilChanged())
          .subscribe((v: any) => {
            this.ga.selectGa(v.slice(0, 4), this.#autofill.loginControlUsed());
            this.parentGroup.get(this.id)?.setValue(v);
          });

        if (this.typeahead?.value) {
          this.ga.selectGa(
            this.typeahead?.value().slice(0, 4),
            this.#autofill.loginControlUsed()
          );
          this.parentGroup.get(this.id)?.setValue(this.typeahead?.value);
        }
      }
    }

    this.#breakpointSub = this.#breakpointObserver
      .observe([
        '(max-width: 709px)',
        '(min-width: 710px)',
        '(min-width: 1080px)',
        '(min-width: 1650px)',
      ])
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.#cdr.detectChanges();
      });

    setTimeout$(() => {
      if (this.type === 'date') {
        const errors: any = this.parentGroup.get(this.id)?.errors;
        this.parentGroup.get(this.id)?.setErrors(null);
        this.parentGroup.get(this.id)?.markAsPristine();
        this.parentGroup.get(this.id)?.markAsUntouched();
        this.parentGroup.get(this.id)?.setErrors(errors);
      }
    }, 15);
  }

  ngOnDestroy(): void {
    if (this.#formDataSub) this.#formDataSub.unsubscribe();
    if (this.#typeaheadSub) this.#typeaheadSub.unsubscribe();
    if (this.#typeaheadSelectSub) this.#typeaheadSelectSub.unsubscribe();
    if (this.#breakpointSub) this.#breakpointSub.unsubscribe();
  }

  get spacerSize(): string {
    const size: string = this.field['#spacer_size'];
    if (size === 'lg') return '30px';
    if (size === 'sm') return '15px';
    if (size === 'xs') return '9px';
    return '';
  }

  /**
   * Trigger click on radio button card
   * @param key
   */
  clickCardRadio(e: any, key: string): any {
    if (e.target?.tagName === 'LABEL') return;
    (document.querySelector(`*[data-id="${key}"] label`) as any)?.click();
  }
  /**
   * Trigger click on checkbox button card
   * @param key
   */
  clickCardCheckbox(e: any, key: string): any {
    (document.querySelector(`*[data-id="${key}"] input`) as any)?.click();
  }
  /**
   * Get the icon for a control
   * @param radioString
   */
  getOptionIcon(key: string): any {
    return this.field[`#option_icon_${key}`] || '';
  }

  /**
   * Get the image for a control
   * @param radioString
   */
  getOptionImage(key: string): any {
    return this.field[`#option_image_${key}`] || '';
  }

  /**
   * Get the text from the radio descripton/title
   * @param radioString
   */
  getText(radioString: string): string {
    if (radioString.includes(' -- ')) {
      return radioString.split(' -- ')[0];
    }
    return radioString;
  }

  /**
   * Get the text from the radio descripton/title
   * @param radioString
   */
  getDescription(radioString: string): string {
    if (radioString.includes(' -- ')) {
      return radioString.split(' -- ')[1];
    }
    return '';
  }

  get options(): KeyValue[] {
    const options = this.field['#options'];

    if (!options) return [];
    if (options instanceof Array) return options;

    const formatted: KeyValue[] = [];

    Object.keys(options).forEach((key: string) => {
      formatted.push({
        key: key,
        value: options[key],
      });
    });

    return formatted;
  }

  submit(): void {
    this.onSubmit.emit();
  }

  errorReplace(haystack: string): string {
    let error = haystack;
    Object.keys(this.errorValues).forEach((needle: string) => {
      if (error) {
        const replace = this.errorValues[needle];
        error = error.replaceAll(`{${needle}}`, replace);
      }
    });
    return error;
  }

  handleCalendarChange(date: Date | null) {
    if (!date) return;
    this.parentGroup.get(this.id)?.setValue(format(date, 'dd.MM.yyyy'));
  }

  get contactAdditional() {
    if (
      this.field['#contact_title_additional'] ||
      this.field['#contact_url_additional']
    ) {
      return {
        title: this.field['#contact_title_additional'] || '',
        url: this.field['#contact_url_additional'] || '',
        description: this.field['#contact_description_additional'] || '',
        icon: this.field['#contact_icon_additional'] || '',
      };
    }

    return null;
  }

  handleFileValueChanges(files: File[] | null): void {
    if (!files || !Array.isArray(files) || files.length === 0) {
      return;
    }

    (files as any)._isMultipleField = !!this.field['#multiple'] || false;
  }

  getInitialMinDate = computed(() => {
    if (!this.minDate) return getDate('today');
    if (new Date(this.minDate).getTime() > new Date().getTime()) {
      return this.minDate;
    } else {
      return getDate('today');
    }
  });
}
